import PropTypes from 'prop-types';
import { basePageWrap } from '../BasePage';
import styles from './PianoLibraryPage.module.scss';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
    useProductSelection,
    setGetArguments,
    getSelectedProductIdFromCookie,
} from '../../utils/singleProductChooser';

// Components
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import HeroButtonModal from '../../components/HeroButtonModal/HeroButtonModal';
import StreamField from '../../components/StreamField';
import AudioPlayerList from '../../components/AudioPlayerList';
import RelatedProductsList from '../../components/RelatedProductsList';
import PianoDownloadTableModal from '../../components/PianoDownloadTableModal';
import TabLinksVertical from '../../components/TabLinksVertical';
import RichText from '../../components/RichText';
import RawHtml from '../../components/RawHtml';

const PianoLibraryPage = ({
    title,
    hero = {},
    breadcrumbs = {
        links: [],
    },
    settings = {
        header: {},
        footer: {},
    },
    content = [],
    modalText,
    soundDemos,
    relatedProducts,
    tags,
    downloads = [],
    tabLinksData = { primary: [], secondary: [] },
    selectedProductId,
    productSelectionOptions,
    richText,
}) => {
    const { selectedProduct, setProductById } = useProductSelection({
        ...productSelectionOptions,
        selectedProductId,
    });

    hero.modal = hero.modal || {};
    hero.modal.buttonText = hero.modal.buttonText || `Download ${hero.title}`;
    const cookieValue = getSelectedProductIdFromCookie();
    const hasSoundDemos =
        soundDemos && soundDemos.items && soundDemos.items.length > 0;

    const tabLinks = {
        ...tabLinksData,
        primary: tabLinksData.primary.map((x) => ({
            ...x,
            href: setGetArguments(x.href, cookieValue ? cookieValue : null),
        })),
    };

    const dataLayerBaseData = {
        event: 'sounds_download',
        collection_name: 'PianoLibraryPage',
        product_name: null,
        sample_name: null,
    };

    const handleDataLayerPushPianoLibraryPage = (download) => {
        const versionName = download.versionName
            ? `${download.size.toUpperCase()} version - ${title} - v${
                  download.versionName
              }`
            : `${download.size.toUpperCase()} version - ${title}`;

        const data = {
            ...dataLayerBaseData,
            sound_name: title,
            version: versionName,
        };

        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push(data);
            console.log('Data pushed to dataLayer:', data);
            console.log('Current dataLayer:', window.dataLayer);
        } else {
            console.error('dataLayer is not defined');
        }
    };

    return (
        <div className={styles['PianoLibraryPage']}>
            <Header {...settings.header} />
            <main>
                <div className={styles['PianoLibraryPage__Container']}>
                    <div className={styles['PianoLibraryPage__Breadcrumbs']}>
                        <Breadcrumbs {...breadcrumbs} />
                    </div>
                    <div
                        className={
                            styles['PianoLibraryPage__ContentContainer']
                        }>
                        <div className={styles['PianoLibraryPage__Desktop']}>
                            {tabLinks && <TabLinksVertical {...tabLinks} />}
                        </div>
                        <div className={styles['PianoLibraryPage__Content']}>
                            {hero && (
                                <HeroButtonModal
                                    {...hero}
                                    modifiers={['alternative']}>
                                    <PianoDownloadTableModal
                                        downloads={downloads}
                                        title={hero.title}
                                        modalText={modalText}
                                        onDownload={
                                            handleDataLayerPushPianoLibraryPage
                                        }
                                    />
                                </HeroButtonModal>
                            )}
                            <div className={styles['PianoLibraryPage__Mobile']}>
                                {tabLinks && <TabLinksVertical {...tabLinks} />}
                            </div>

                            {richText && (
                                <div
                                    className={
                                        styles['PianoLibraryPage__RichText']
                                    }>
                                    <RawHtml html={richText} />
                                </div>
                            )}

                            {hasSoundDemos && (
                                <AudioPlayerList
                                    download={false}
                                    column={true}
                                    {...soundDemos}
                                />
                            )}
                            {content && <StreamField items={content} />}
                            {relatedProducts && (
                                <RelatedProductsList {...relatedProducts} />
                            )}
                        </div>
                    </div>
                </div>
            </main>
            <Footer {...settings.footer} />
        </div>
    );
};

PianoLibraryPage.propTypes = {
    settings: PropTypes.object,
    hero: PropTypes.object,
    breadcrumbs: PropTypes.object,
    tabLinksData: PropTypes.shape({
        primary: PropTypes.array,
        secondary: PropTypes.array,
    }),
    content: PropTypes.arrayOf(PropTypes.object),
    relatedProducts: PropTypes.object,
};

export default basePageWrap(PianoLibraryPage);
